<template>
  <main class="purchase-receipt" :data-status="status">
    <div class="checkout-breadcrumb"><span>Tu compra</span><span aria-hidden="true">/</span><strong>Confirmación</strong><span class="checkout-breadcrumb__end"><ReceiptIcon :name="approved ? 'check' : 'clock'" />{{ state.badge }}</span></div>
    <div class="confirmation-layout confirmation-layout--receipt-first">
      <section class="confirmation-hero receipt-confirmation-hero" aria-labelledby="purchase-confirmation-title">
        <div class="confirmation-hero__copy">
          <div class="approval-tag"><span><ReceiptIcon :name="approved ? 'check' : 'clock'" /></span>{{ state.badge }}</div>
          <h1 id="purchase-confirmation-title">{{ title || state.title }}</h1>
        </div>
        <div class="confirmation-actions">
          <slot name="actions">
          <ReceiptDownloadButton v-if="approved" id="download-tickets" ref="primaryAction" :count="order.ticketCount || 0" :ready="downloadReady" :label="downloadLabel" :disabled="downloadDisabled" @click="$emit('download')" />
          <FomoButton v-else class="download-primary return-event" variant="secondary" icon="" @click="$emit('back')"><span class="download-label"><ReceiptIcon name="arrow" />Volver al evento</span></FomoButton>
          <button class="copy-button" type="button" @click="$emit('copy')"><ReceiptIcon name="link" />{{ approved ? 'Copiar descarga' : 'Copiar link del evento' }}</button>
          </slot>
        </div>
        <div class="confirmation-intro"><p>{{ description || state.description }}</p></div>
      </section>

      <aside class="printer-scene" aria-label="Recibo de tu compra">
        <div class="printer-scene__heading"><div><span>{{ approved ? 'TU RECIBO' : 'REGISTRO DE TU COMPRA' }}</span><small v-if="order.number">{{ order.number }}</small></div><button v-if="!loading" class="printer-replay" type="button" @click="replay += 1"><ReceiptIcon name="replay" />Repetir</button></div>
        <div v-if="loading" class="receipt-loading" role="status">Cargando los datos de tu compra…</div>
        <PurchaseReceiptPrinter v-else :order="order" :state="state" :replay="replay" />
        <p v-if="invoiceError" class="receipt-message" role="status">{{ invoiceError }} <button type="button" @click="$emit('retry')">Reintentar</button></p>
      </aside>

      <section class="confirmation-summary" aria-label="Resumen y estado de tu compra">
        <div class="event-summary">
          <span v-if="order.category" class="event-summary__category">{{ order.category }}</span>
          <h2>{{ order.event }}</h2>
          <div class="event-fact"><ReceiptIcon name="calendar" /><span>{{ order.date }}<small v-if="order.time">{{ order.time }} · {{ order.timeZone }}</small></span></div>
          <div v-if="order.venue || order.city" class="event-fact"><ReceiptIcon name="pin" /><span>{{ order.venue }}<small>{{ order.city }}</small></span></div>
          <div class="event-summary__bottom"><span v-if="order.ticketCount"><ReceiptIcon name="ticket" />{{ ticketCountLabel }}</span><span class="active-download"><i></i>{{ deliveryLabel || (approved ? (downloadReady ? 'Link de descarga activo' : 'Preparando descarga') : state.badge) }}</span></div>
        </div>
        <div class="confirmation-followup">
          <ol class="purchase-progress" aria-label="Estado de tu compra"><li v-for="(step,index) in state.steps" :key="step.title" :class="'is-' + step.state"><span class="progress-number">0{{ index+1 }}</span><div><strong>{{ step.title }}</strong><small>{{ step.detail }}</small></div></li></ol>
          <div class="email-note"><ReceiptIcon name="mail" /><p>{{ state.email }} <router-link v-if="approved" :to="{ name: 'usertickets' }">Mis Tickets ↗</router-link><strong v-else-if="status !== 'failed'">Mis Tickets.</strong></p></div>
          <p v-if="feedback" class="receipt-message" role="status">{{ feedback }}</p>
          <div v-if="showSharing" class="share-row"><span>Un buen plan se comparte</span><button type="button" @click="$emit('whatsapp')"><ReceiptIcon name="whatsapp" />Estado WA</button><button type="button" @click="$emit('instagram')"><ReceiptIcon name="instagram" />Historia IG</button></div>
          <slot name="extra" />
        </div>
      </section>
    </div>
    <div v-if="approved && showDownloadDock" class="download-dock" role="region" aria-label="Descarga de entradas"><div><strong>{{ ticketCountLabel || 'Tus entradas' }}</strong><span>{{ downloadReady ? 'Descarga disponible' : 'Preparando descarga' }}</span></div><button id="download-sticky" type="button" :disabled="downloadDisabled" @click="$emit('download')"><ReceiptIcon name="download" />{{ downloadLabel }}</button></div>
    <p v-if="feedback && showDownloadDock" class="receipt-toast" role="status">{{ feedback }}</p>
  </main>
</template>

<script>
import FomoButton from '../ui/FomoButton.vue'
import ReceiptDownloadButton from './ReceiptDownloadButton.vue'
import ReceiptIcon from './ReceiptIcon.vue'
import PurchaseReceiptPrinter from './PurchaseReceiptPrinter.vue'
import { purchaseReceiptState } from '../../common/purchaseReceiptState'

export default {
  name: 'PurchaseReceipt',
  components: { FomoButton, ReceiptDownloadButton, ReceiptIcon, PurchaseReceiptPrinter },
  props: {
    order: { type: Object, required: true },
    status: { type: String, default: 'pending' },
    stateOverride: { type: Object, default: null },
    deliveryLabel: { type: String, default: '' },
    showSharing: { type: Boolean, default: true },
    title: { type: String, default: '' }, description: { type: String, default: '' },
    loading: Boolean, downloadDisabled: Boolean, downloadReady: Boolean,
    downloadLabel: { type: String, default: 'Descargar entradas' },
    feedback: { type: String, default: '' }, invoiceError: { type: String, default: '' },
  },
  emits: ['download', 'copy', 'back', 'whatsapp', 'instagram', 'retry'],
  data() { return { replay: 0, showDownloadDock: false, downloadObserver: null } },
  computed: {
    approved() { return this.status === 'approved' },
    state() { return { ...purchaseReceiptState(this.status, this.downloadReady), ...(this.stateOverride || {}) } },
    ticketCountLabel() { return this.order.ticketCount ? `${this.order.ticketCount} ${this.order.ticketCount === 1 ? 'entrada' : 'entradas'}` : '' },
  },
  watch: { status() { this.$nextTick(this.observeDownload) } },
  mounted() { this.observeDownload() },
  beforeUnmount() { this.downloadObserver?.disconnect() },
  methods: {
    observeDownload() {
      this.downloadObserver?.disconnect()
      this.showDownloadDock = false
      const action = this.$refs.primaryAction?.$el
      if (!action || !('IntersectionObserver' in window)) return
      this.downloadObserver = new IntersectionObserver(([entry]) => { this.showDownloadDock = !entry.isIntersecting && entry.boundingClientRect.bottom < 0 })
      this.downloadObserver.observe(action)
    },
  },
}
</script>

<style src="./purchase-receipt.css"></style>
<style src="./receipt-confirmation-hero.css"></style>
